// src/components/outreach/KanbanBoard.tsx
import { useEffect, useState } from 'react';
import {
  DndContext,
  closestCorners,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Pitch, PitchStatus } from '../../types/outreach';
import { BoardColumn } from './BoardColumn';
import { PitchCard } from './PitchCard';
import { useUpdatePitchStatus } from '../../react-query/outreach-queries';
import ComposePitchModal from '../podcasts/ComposePitchModal';

const COLUMNS: { id: PitchStatus; title: string }[] = [
  { id: 'DRAFT', title: 'Draft' },
  { id: 'PITCH_SENT', title: 'Pitch Sent' },
  { id: 'FOLLOW_UP_SENT', title: 'Follow Up' },
  { id: 'REPLIED', title: 'Replied' },
  { id: 'ACCEPTED', title: 'Accepted' },
  { id: 'REJECTED', title: 'Rejected' },
];

interface KanbanBoardProps {
  pitches: Pitch[];
  campaignId: string;
}

export function KanbanBoard({ pitches: initialPitches }: KanbanBoardProps) {
    const { mutate: updateStatus } = useUpdatePitchStatus();
    const [items, setItems] = useState<Pitch[]>(initialPitches);
    const [activePitch, setActivePitch] = useState<Pitch | null>(null);

    // Modal state
    const [composeOpen, setComposeOpen] = useState(false);
    const [selectedPitch, setSelectedPitch] = useState<Pitch | null>(null);
    
    const handleCompose = (pitch: Pitch) => {
        setSelectedPitch(pitch);
        setComposeOpen(true);
    };

    useEffect(() => {
        setItems(initialPitches);
    }, [initialPitches]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const pitch = items.find(p => p.id === active.id);
        if (pitch) setActivePitch(pitch);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActivePitch(null);

        if (!over) return;

        const activeId = active.id as string;
        let targetStatus: PitchStatus | null = null;

        if (COLUMNS.find(c => c.id === over.id)) {
             targetStatus = over.id as PitchStatus;
        } else {
             const overPitch = items.find(p => p.id === over.id);
             if (overPitch) {
                 targetStatus = overPitch.status;
             }
        }

        if (targetStatus) {
            const currentPitch = items.find(p => p.id === activeId);
            if (currentPitch && currentPitch.status !== targetStatus) {
                // Optimistic visual update
                setItems((prev) => 
                     prev.map(p => p.id === activeId ? { ...p, status: targetStatus! } : p)
                );
                
                // Server update
                updateStatus({ pitchId: activeId, data: { status: targetStatus } });
            }
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="flex gap-4 h-full overflow-x-auto pb-4 items-start">
                {COLUMNS.map((col) => (
                    <BoardColumn 
                        key={col.id} 
                        id={col.id} 
                        title={col.title} 
                        pitches={items.filter(p => p.status === col.id)} 
                        onCompose={handleCompose}
                    />
                ))}
            </div>

            <DragOverlay>
                {activePitch ? <PitchCard pitch={activePitch} overlay /> : null}
            </DragOverlay>

            {selectedPitch && (
                <ComposePitchModal
                    open={composeOpen}
                    onClose={() => {
                        setComposeOpen(false);
                        setSelectedPitch(null);
                    }}
                    pitchId={selectedPitch.id}
                    podcastId={selectedPitch.podcastId}
                    recipientEmail={selectedPitch.podcast?.email}
                    initialSubject={selectedPitch.draftSubject || `Collaboration with ${selectedPitch.podcast?.title}`}
                    initialBody={selectedPitch.draftBody}
                    onSuccess={() => {
                         if (selectedPitch.status === 'DRAFT') {
                            setItems(prev => prev.map(p => p.id === selectedPitch.id ? { ...p, status: 'PITCH_SENT' } : p));
                            updateStatus({ pitchId: selectedPitch.id, data: { status: 'PITCH_SENT' } });
                         }
                    }}
                />
            )}
        </DndContext>
    );
}
